import { makeAutoObservable, observable, computed } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { WhatsappBusinessApiHeaderType } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";

type ButtonItem = { type: "QUICK_REPLY" | "URL"; text: string; url: string };

export const WABA_TEMPLATE_BUTTON_TEXT_MAX_LENGTH = 25;

export class CreateTemplateModalStore {
  name = "";
  wabaOption: { label: string; value: string } | undefined = undefined;
  category = "UTILITY";
  language = "en_US";
  headerType: WhatsappBusinessApiHeaderType = "NONE";
  headerText = "";
  bodyText = "";
  bodyParameterSamples: Record<number, string> = {};
  footerText = "";
  buttons: ButtonItem[] = [];
  isSubmitting = false;
  headerMediaFile: File | null = null;
  headerMediaUpload: IPromiseBasedObservable<{ handle: string }> | null = null;

  constructor() {
    makeAutoObservable(this, {
      buttons: observable.shallow,
      hasButtonTextLengthError: computed,
      headerMediaFile: observable.ref,
      headerMediaUpload: observable.ref,
    });
  }

  setName(name: string) {
    this.name = name;
  }

  setWabaOption(value: { label: string; value: string }) {
    this.wabaOption = value;
  }

  setCategory(category: string) {
    this.category = category;
  }

  setLanguage(language: string) {
    this.language = language;
  }

  setHeaderType(type: WhatsappBusinessApiHeaderType) {
    this.headerType = type;
    this.headerText = "";
    this.clearHeaderMedia();
  }

  setHeaderText(text: string) {
    this.headerText = text;
  }

  setBodyText(text: string) {
    this.bodyText = text;
  }

  setFooterText(text: string) {
    this.footerText = text;
  }

  setParameterSample(n: number, value: string) {
    this.bodyParameterSamples = { ...this.bodyParameterSamples, [n]: value };
  }

  setHeaderMediaFile(file: File | null) {
    this.headerMediaFile = file;
    if (!file) {
      this.headerMediaUpload = null;
    }
  }

  clearHeaderMedia() {
    this.headerMediaFile = null;
    this.headerMediaUpload = null;
  }

  uploadHeaderMedia(upload: (wabaId: string, file: File) => Promise<{ handle: string }>, file: File) {
    this.headerMediaFile = file;

    if (!this.wabaOption) {
      this.headerMediaUpload = null;
      return;
    }

    this.headerMediaUpload = fromPromise(upload(this.wabaOption.value, file));
  }

  addButton() {
    if (this.buttons.length >= 3) return;
    this.buttons = [...this.buttons, { type: "QUICK_REPLY", text: "", url: "" }];
  }

  removeButton(index: number) {
    this.buttons = this.buttons.filter((_, i) => i !== index);
  }

  updateButton(index: number, field: keyof ButtonItem, value: string) {
    this.buttons = this.buttons.map((btn, i) => (i === index ? { ...btn, [field]: value } : btn));
  }

  get isMediaHeaderType(): boolean {
    return this.headerType === "IMAGE" || this.headerType === "VIDEO" || this.headerType === "DOCUMENT";
  }

  get detectedParameters(): number[] {
    const matches = this.bodyText.matchAll(/\{\{(\d+)\}\}/g);
    const nums = new Set<number>();
    for (const match of matches) {
      nums.add(Number(match[1]));
    }
    return [...nums].sort((a, b) => a - b);
  }

  get hasButtonTextLengthError(): boolean {
    return this.buttons.some((btn) => btn.text.length > WABA_TEMPLATE_BUTTON_TEXT_MAX_LENGTH);
  }

  get isValidInput(): boolean {
    if (!/^[a-z0-9_]+$/.test(this.name)) return false;
    if (!this.wabaOption) return false;
    if (!this.category) return false;
    if (!this.language) return false;
    if (!this.bodyText.trim()) return false;
    if (this.headerType === "TEXT" && this.headerText.length > 60) return false;
    if (this.isMediaHeaderType && this.headerMediaUpload?.state !== "fulfilled") return false;
    if (this.footerText.length > 60) return false;
    if (this.hasButtonTextLengthError) return false;
    for (const param of this.detectedParameters) {
      if (!this.bodyParameterSamples[param]?.trim()) return false;
    }
    for (const btn of this.buttons) {
      if (!btn.text.trim()) return false;
      if (btn.type === "URL" && !btn.url.trim()) return false;
    }
    return true;
  }

  get submitData() {
    const components: any[] = [];

    if (this.headerType === "TEXT" && this.headerText) {
      components.push({
        type: "HEADER",
        format: "TEXT",
        text: this.headerText,
      });
    } else if (this.isMediaHeaderType && this.headerMediaUpload?.state === "fulfilled") {
      components.push({
        type: "HEADER",
        format: this.headerType,
        example: { header_handle: [this.headerMediaUpload.value.handle] },
      });
    }

    const bodyComponent: any = { type: "BODY", text: this.bodyText };
    if (this.detectedParameters.length > 0) {
      bodyComponent.example = {
        body_text: [this.detectedParameters.map((n) => this.bodyParameterSamples[n] || `{{${n}}}`)],
      };
    }
    components.push(bodyComponent);

    if (this.footerText) {
      components.push({ type: "FOOTER", text: this.footerText });
    }

    if (this.buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons: this.buttons.map((btn) =>
          btn.type === "QUICK_REPLY" ? { type: "QUICK_REPLY", text: btn.text } : { type: "URL", text: btn.text, url: btn.url }
        ),
      });
    }

    return {
      wabaId: this.wabaOption!.value,
      name: this.name,
      language: this.language,
      category: this.category,
      components,
    };
  }

  get previewData() {
    let bodyTextWithSamples = this.bodyText;
    for (const n of this.detectedParameters) {
      const sample = this.bodyParameterSamples[n];
      if (sample) {
        bodyTextWithSamples = bodyTextWithSamples.replace(new RegExp(`\\{\\{${n}\\}\\}`, "g"), sample);
      }
    }

    return {
      headerText: this.headerType === "TEXT" ? this.headerText : "",
      headerMediaType: this.isMediaHeaderType ? this.headerType : undefined,
      bodyTextWithSamples,
      footerText: this.footerText,
      buttons: this.buttons,
    };
  }
}
